import React from 'react'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import isBool from 'lodash/isBoolean'
import FormsyForm from 'formsy-react'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../Page/connect'
import { connect as connectStaticText } from '../../StaticText/connect'
import {
  H3, H5, HEADING_TYPES,
} from '../../Heading/index'
import Button, { TYPES as BUTTON_TYPES } from '../../Button/index'
import {
  ACCOUNT_TYPE_MYQEE,
  isTaxable,
} from '../../../services/user-account/index'
import Link from '../../Link/index'
import { TARGET_BLANK } from '../../Link/constants'
import {
  getFormattedLocaleDate,
  dateIsFuture,
} from '../../../services/date-time/index'
import { formatPrice, getFreeTrialPeriod, PLAN_CATEGORY_AI_ONLY } from '../../../services/plans/index'
import CartBillingProcessing from '../../CartBillingProcessing/index'
import { URL_ACCOUNT_PROFILE } from '../../../services/url/constants'
import { getHelpCenterUrl, getConfirmationFinishUrl } from '../../../services/url/index'
import { getPrimary } from '../../../services/languages/index'
import { GIFT_SUBSCRIPTION_STATE } from '../../../services/gift/index'
import { getBoundActions } from '../../../actions/index'
import { TextInput } from '../../FormInput.v2/index'
import CartOrderSummaryV2 from '../CartOrderSummaryV2/index'
import { CHECKOUT_UPDATE_EMAIL_GA4_EVENT } from '../../../services/event-tracking/index'
import { EUR, CAD } from '../../../services/currency/index'
import { FR } from '../../../services/languages/constants'
import { getUserName } from '../../../services/user/index'
import CartSinglePageConfirmation from '../../CartSinglePage/CartSinglePageConfirmation/CartSinglePageConfirmation'

/**
 * Checkout confirmation page
 * @param {Map} props.onboardingCompleted - Onboarding completed state
 * @param {Map} props.userAccount - User account state
 * @param {Map} props.staticText - Static text state
 * @param {Map} props.checkout - Checkout state
 * @param {Map} props.plans - Plans state
 * @param {Map} props.user - User state
 * @param {Function} props.setEventDefault - Function to set default GA4 event
 */
function CartConfirmationPage (props) {
  const {
    onboardingCompleted,
    setEventDefault,
    userAccount,
    staticText,
    checkout,
    plans,
    user,
  } = props
  const isWinbackCheckout = checkout.get('flow') === '/checkout'

  if (isWinbackCheckout) {
    return <CartSinglePageConfirmation />
  }

  // only render page when we have user, billing, and onboarding data
  // else the experience looks weird and 'undefined' variables can show up
  const userAccountDetails = userAccount.getIn(['details', 'data'], Map()) || Map()
  const paidThroughDate = userAccountDetails.getIn(['billing', 'subscriptions', 'paidThroughDate'])
  const planId = userAccountDetails.getIn(['billing', 'subscriptions', 'plan', 'productRatePlanId'])
  const nextPlanId = userAccountDetails.getIn(['billing', 'subscriptions', 'nextPlan', 'productRatePlanId'])
  const giftPaidThroughDate = userAccountDetails.getIn(['gift', 'paidThroughDate'])
  const userData = user.get('data') || Map()
  const planCategory = userAccountDetails.getIn(['billing', 'subscriptions', 'plan', 'planCategory'])
  const isAiOnly = planCategory === PLAN_CATEGORY_AI_ONLY
  const isLoaded = isBool(onboardingCompleted)
    && Map.isMap(userAccountDetails)
    && userAccountDetails.size > 0
    && paidThroughDate
    && (planId || nextPlanId)
    && Map.isMap(userData)
    && userData.size > 0
  const preTax = isTaxable(props) ? ` (${staticText.getIn(['data', 'preTax'])})` : null

  const email = user.getIn(['updateUser', 'email']) || user.getIn(['data', 'mail'])
  const firstName = user.getIn(['updateUser', 'firstName']) || user.getIn(['data', 'firstName']) || getUserName(user.get('data'))
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const giftSubscriptionActive = userAccountDetails.getIn(['gift', 'status']) === GIFT_SUBSCRIPTION_STATE.ACTIVE
  // the is a temporary fix because right now Zoura has an invoice timeout issue.
  // AccountPaidThroughDate should be all we need but we are hacking the correct date
  // until a more involved fix is in place for long async checkout transactions

  const planFreeTrialPeriod = getFreeTrialPeriod(plans.getIn(['selection', 'segments'], List()))
  let nextBillingDate = getFormattedLocaleDate(
    giftSubscriptionActive ? giftPaidThroughDate : paidThroughDate,
    userLanguage,
  )
  const selectedPlan = Map.isMap(plans.get('selection')) ? plans.get('selection') : Map()
  const currencyIso = selectedPlan.get('currencyIso')
  let showCurrencyIso = true

  if (currencyIso === EUR || (currencyIso === CAD && userLanguage === FR)) {
    showCurrencyIso = false
  }

  const orderData = Map.isMap(checkout.get('orderData')) ? checkout.get('orderData') : Map()
  const isInPlanFreeTrialPeriod = !!planFreeTrialPeriod

  // start myqee
  const userAcquisition = (List.isList(user.get('userAcquisitions')) ? user.get('userAcquisitions') : List()).first() || Map()
  const userAcquisitionSource = userAcquisition.get('source')
  const isMyqeeUser = userAcquisitionSource === ACCOUNT_TYPE_MYQEE
  const myqeePaidThroughDate = userAcquisition.get('paidThroughDate')
  const myqeePaidThroughDateInFuture = myqeePaidThroughDate && dateIsFuture(myqeePaidThroughDate)
  const showMyqee = isMyqeeUser && myqeePaidThroughDateInFuture

  // The next bill date is in the future?
  const isNextBillDateInFuture = isInPlanFreeTrialPeriod
    || myqeePaidThroughDateInFuture
    || giftSubscriptionActive
  const costs = List.isList(selectedPlan.get('costs')) ? selectedPlan.get('costs') : List(['0'])
  const initialPrice = isNextBillDateInFuture ? '0' : costs.first()
  const finalPrice = costs.last()
  const formattedInitialPlanPrice = `${formatPrice(
    currencyIso,
    initialPrice,
    showCurrencyIso,
    null,
    userLanguage,
  )}${preTax && !isNextBillDateInFuture ? preTax : ''}`
  const formattedFinalPlanPrice = `${formatPrice(
    currencyIso,
    finalPrice,
    showCurrencyIso,
    null,
    userLanguage,
  )}${preTax || ''}`
  // this is for plans that have a first segment that is not zero,
  // but also have a second segment
  const costsEqual = initialPrice === finalPrice

  if (showMyqee) {
    nextBillingDate = getFormattedLocaleDate(myqeePaidThroughDate, userLanguage)
  }
  // end myqee

  const onClickEmailLink = () => {
    // GA4 event
    setEventDefault(CHECKOUT_UPDATE_EMAIL_GA4_EVENT)
  }

  const renderConfirmationTitle = () => {
    return (
      <H3 className="cart-confirmation__title">
        {`${staticText.getIn(['data', 'welcome'])}, ${firstName}!`}
      </H3>
    )
  }

  const renderConfirmationSummary = () => {
    const nextBillingText = isNextBillDateInFuture
      ? `${staticText.getIn(['data', 'dueOn'])} ${nextBillingDate}`
      : `${staticText.getIn(['data', 'nextBillingDateIs'])} ${nextBillingDate}`

    return (
      <React.Fragment>
        <div className="cart-confirmation__summary-footer-info cart-confirmation__summary-footer-info--billed">
          <div className={`cart-confirmation__summary-footer-label${!isNextBillDateInFuture ? ' cart-confirmation__summary-footer-label--bold' : ''}`}>
            {`${staticText.getIn(['data', 'billedToday'])}:`}
          </div>
          <div className={`cart-confirmation__summary-footer-price${!isNextBillDateInFuture ? ' cart-confirmation__summary-footer-price--bold' : ''}`}>
            {formattedInitialPlanPrice}
          </div>
        </div>
        <div className="cart-confirmation__summary-footer-info cart-confirmation__summary-footer-info--next-date">
          <div className={`cart-confirmation__summary-footer-label${isNextBillDateInFuture || !costsEqual ? ' cart-confirmation__summary-footer-label--bold' : ' cart-confirmation__summary-footer-label--full-width'}`}>
            {nextBillingText}
          </div>
          {
            !costsEqual
              ? <div className="cart-confirmation__summary-footer-price cart-confirmation__summary-footer-price--bold">
                  {formattedFinalPlanPrice}
                </div>
              : null
          }
        </div>
      </React.Fragment>
    )
  }

  if (orderData.size === 0) {
    return null
  }

  if (orderData.size > 0 && !isLoaded) {
    return (
      <div className="cart-confirmation">
        <div className="cart-confirmation__sherpa">
          <CartBillingProcessing finalize />
        </div>
      </div>
    )
  }

  return (
    <div className="cart-confirmation">
      <div className="cart-confirmation__wrapper">
        <div className="cart-confirmation__background">
          {renderConfirmationTitle()}
        </div>
        <div className="cart-confirmation__content">
          <header>
            {renderConfirmationTitle()}
            <p className="cart-confirmation__sub-title">
              {staticText.getIn(['data', 'confirmationEmail'])}
            </p>
          </header>

          <div className="cart-confirmation__email">
            <FormsyForm className="cart-confirmation__email-form">
              <div className="cart-confirmation__email-container">
                <TextInput
                  label={staticText.getIn(['data', 'email'])}
                  autocomplete="off"
                  value={email}
                  name="cartConfirmationPageEmail"
                  readonly
                  block
                />
                <Link
                  to={URL_ACCOUNT_PROFILE}
                  onClick={onClickEmailLink}
                  className="cart-confirmation__text-link"
                >
                  {staticText.getIn(['data', 'change'])}
                </Link>
              </div>
            </FormsyForm>
          </div>

          <H5 as={HEADING_TYPES.H6} className="cart-confirmation__heading">
            {staticText.getIn(['data', 'planInfo'])}:
          </H5>

          <div className="cart-confirmation__summary">
            <CartOrderSummaryV2
              footer={renderConfirmationSummary()}
            />
          </div>

          <div className="cart-confirmation__help-text">
            <p>
              {`${staticText.getIn(['data', 'furtherAssistance'])} ${staticText.getIn(['data', 'helpText'])} `}
              <Link
                target={TARGET_BLANK}
                to={getHelpCenterUrl(userLanguage)}
                className="cart-confirmation__text-link"
                directLink
              >
                {staticText.getIn(['data', 'helpCenter'])}
              </Link>
              .
            </p>
          </div>
          <Button
            url={getConfirmationFinishUrl(onboardingCompleted, isAiOnly)}
            type={BUTTON_TYPES.PRIMARY}
            className="cart-confirmation__get-started"
            data-action="cart-confirmation-get-started"
          >
            {staticText.getIn(['data', 'getStarted'])}
          </Button>
        </div>
      </div>
    </div>
  )
}

CartConfirmationPage.propTypes = {
  plans: ImmutablePropTypes.map.isRequired,
  checkout: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectStaticText({ storeKey: 'cartConfirmationPage' }),
  connectRedux(
    (state) => ({
      onboardingCompleted: state.onboarding.get('completed'),
      userAccount: state.userAccount,
      checkout: state.checkout,
      plans: state.plans,
      auth: state.auth,
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(CartConfirmationPage)
